import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardActionArea,
  CircularProgress,
  Avatar,
  Chip,
  Stack,
  IconButton,
} from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { getCategories } from "../../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../../services/indexMediaBaseUrl";

const CategoriesSection = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { t, i18n } = useTranslation();

  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const isAr = lang === "ar";

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await getCategories();
        const list = response?.data?.data || response?.data || [];
        setCategories(Array.isArray(list) ? list : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const getName = (cat) =>
    isAr ? cat?.name_ar || cat?.name_en || "" : cat?.name_en || cat?.name_ar || "";

  const getDesc = (cat) =>
    isAr ? cat?.desc_ar || cat?.desc_en || "" : cat?.desc_en || cat?.desc_ar || "";

  // parents = category_id is null and status !== 0
  const parents = categories.filter(
    (c) => (c.category_id === null || c.category_id === undefined) && c.status !== 0
  );

  if (loading) {
    return (
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress size={44} sx={{ color: "primary.main" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="error" variant="body1" fontWeight={600}>
          {isAr ? "حدث خطأ في تحميل الفئات" : "Error loading categories"}
        </Typography>
      </Box>
    );
  }

  if (!parents.length) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary" fontWeight={600}>
          {isAr ? "لا توجد فئات متاحة" : "No categories available"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: { xs: 4, md: 7 },
        position: "relative",
        overflow: "hidden",
        bgcolor: "background.paper",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background:
            "linear-gradient(90deg, transparent 0%, #d4af37 50%, transparent 100%)",
        },
      }}
    >
      <Container maxWidth="xl">
        {/* Section Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            mb: { xs: 3, md: 5 },
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #d4af37 0%, #f4e5c2 100%)",
              borderRadius: "50%",
              p: 1.2,
              display: "flex",
              boxShadow: "0 4px 16px rgba(212, 175, 55, 0.35)",
            }}
          >
            <CategoryIcon sx={{ fontSize: { xs: 24, md: 30 }, color: "#fff" }} />
          </Box>
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{
              fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2.2rem" },
              background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
            }}
          >
            {isAr ? "تصفح الفئات" : "Browse Categories"}
          </Typography>
        </Box>

        {/* Categories List */}
        <Stack spacing={{ xs: 3, md: 4 }}>
          {parents.map((parent) => {
            const children = Array.isArray(parent.children_recursive)
              ? parent.children_recursive.filter((c) => c.status !== 0)
              : [];

            return (
              <Box key={parent.id}>
                {/* Parent Header Card */}
                <Card
                  component={Link}
                  to={`/category/${parent.id}`}
                  elevation={0}
                  sx={{
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1.5, md: 2.5 },
                    p: { xs: 2, md: 3 },
                    mb: children.length > 0 ? 2 : 0,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "grey.100",
                    bgcolor: "background.paper",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 6px 24px rgba(212, 175, 55, 0.15)",
                      borderColor: "primary.light",
                      transform: "translateY(-2px)",
                      "& .parent-arrow": {
                        color: "primary.main",
                        transform: isAr ? "translateX(-4px)" : "translateX(4px)",
                      },
                    },
                  }}
                >
                  <Avatar
                    src={getImageUrl(parent.image)}
                    alt={getName(parent)}
                    variant="rounded"
                    sx={{
                      width: { xs: 52, md: 72 },
                      height: { xs: 52, md: 72 },
                      border: "2px solid",
                      borderColor: "primary.light",
                      boxShadow: "0 2px 8px rgba(212, 175, 55, 0.2)",
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      sx={{
                        color: "text.primary",
                        fontSize: { xs: "1rem", md: "1.25rem" },
                        mb: 0.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getName(parent)}
                    </Typography>
                    {getDesc(parent) && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: { xs: "0.75rem", md: "0.85rem" },
                        }}
                      >
                        {getDesc(parent)}
                      </Typography>
                    )}
                    {children.length > 0 && (
                      <Typography
                        variant="caption"
                        color="primary.main"
                        fontWeight={700}
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {children.length} {isAr ? "فئة فرعية" : "subcategories"}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    className="parent-arrow"
                    sx={{
                      color: "grey.400",
                      transition: "all 0.3s ease",
                      flexShrink: 0,
                    }}
                  >
                    {isAr ? (
                      <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
                    )}
                  </Box>
                </Card>

                {/* Children — horizontal scroll on mobile, grid on desktop */}
                {children.length > 0 && (
                  <Box
                    sx={{
                      // on mobile: horizontal scroll
                      display: { xs: "flex", md: "grid" },
                      gridTemplateColumns: {
                        md: "repeat(3, 1fr)",
                        lg: "repeat(4, 1fr)",
                      },
                      gap: { xs: 1.5, md: 2 },
                      overflowX: { xs: "auto", md: "visible" },
                      pb: { xs: 1.5, md: 0 },
                      px: { xs: 0.5, md: 0 },
                      scrollSnapType: { xs: "x mandatory", md: "none" },
                      "&::-webkit-scrollbar": { height: 4 },
                      "&::-webkit-scrollbar-thumb": {
                        bgcolor: "grey.300",
                        borderRadius: 2,
                      },
                    }}
                  >
                    {children.map((child) => {
                      const grandchildren = Array.isArray(child.children_recursive)
                        ? child.children_recursive.filter((g) => g.status !== 0)
                        : [];

                      return (
                        <Card
                          key={child.id}
                          elevation={0}
                          sx={{
                            // mobile: fixed width card snapshot
                            minWidth: { xs: 220, sm: 260, md: "auto" },
                            maxWidth: { xs: 280, md: "none" },
                            scrollSnapAlign: { xs: "start", md: "unset" },
                            flexShrink: 0,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "grey.100",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                              borderColor: "primary.light",
                              "& .child-img": {
                                transform: "scale(1.1)",
                              },
                            },
                          }}
                        >
                          <CardActionArea
                            component={Link}
                            to={`/category/${child.id}`}
                            sx={{
                              p: { xs: 1.5, md: 2 },
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              height: "100%",
                            }}
                          >
                            {/* Child header */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                mb: 1.5,
                                width: "100%",
                              }}
                            >
                              <Avatar
                                src={getImageUrl(child.image)}
                                alt={getName(child)}
                                variant="rounded"
                                className="child-img"
                                sx={{
                                  width: { xs: 44, md: 52 },
                                  height: { xs: 44, md: 52 },
                                  border: "2px solid",
                                  borderColor: "primary.light",
                                  transition: "transform 0.3s ease",
                                  flexShrink: 0,
                                }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={700}
                                  sx={{
                                    color: "text.primary",
                                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {getName(child)}
                                </Typography>
                                {getDesc(child) && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: "block",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      fontSize: "0.7rem",
                                    }}
                                  >
                                    {getDesc(child)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            {/* Grandchildren chips */}
                            {grandchildren.length > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  flexWrap: "wrap",
                                  mt: "auto",
                                  pt: 1.5,
                                  borderTop: "1px solid",
                                  borderColor: "grey.50",
                                  width: "100%",
                                }}
                              >
                                {grandchildren.slice(0, 3).map((grand) => (
                                  <Chip
                                    key={grand.id}
                                    label={getName(grand)}
                                    size="small"
                                    component={Link}
                                    to={`/category/${grand.id}`}
                                    clickable
                                    avatar={
                                      grand.image ? (
                                        <Avatar
                                          src={getImageUrl(grand.image)}
                                          sx={{ width: 20, height: 20 }}
                                        />
                                      ) : undefined
                                    }
                                    sx={{
                                      bgcolor: "grey.50",
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      height: 26,
                                      transition: "all 0.2s ease",
                                      "& .MuiChip-label": { px: 1 },
                                      "&:hover": {
                                        bgcolor: "primary.50",
                                        color: "primary.dark",
                                        transform: "scale(1.03)",
                                      },
                                    }}
                                  />
                                ))}
                                {grandchildren.length > 3 && (
                                  <Chip
                                    label={`+${grandchildren.length - 3}`}
                                    size="small"
                                    sx={{
                                      bgcolor: "primary.main",
                                      color: "#fff",
                                      fontWeight: 700,
                                      fontSize: "0.7rem",
                                      height: 26,
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                          </CardActionArea>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Container>
    </Box>
  );
};

export default CategoriesSection;
